import {
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

export interface ErrorBoundaryProps {
  logger?: (error: Error, info: ErrorInfo) => void;
  /**
   * Fallback content, e.g. `errorView={<ErrorPage />}`. Pass `null` to fail
   * silently.
   *
   * A `ReactNode`, not a component type — passing a component reference
   * (`errorView={ErrorPage}`) is a type error on purpose, since it would be
   * called as a plain function and break hooks.
   */
  errorView: ReactNode;
}

interface State {
  error?: Error;
}

/**
 * Catches render errors in its subtree and shows `errorView` instead.
 *
 * Without a `logger` the error is written to the console.
 */
export class ErrorBoundary extends Component<
  PropsWithChildren<ErrorBoundaryProps>,
  State
> {
  constructor(props: PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { logger } = this.props;

    if (!logger) {
      console.error(error, info);
      return;
    }

    logger(error, info);
  }

  render() {
    const { error } = this.state;
    const { children, errorView } = this.props;

    return error ? errorView : children;
  }
}
